import IconButton from 'components/atoms/button/IconButton'
import Form from 'components/atoms/form/Form'
import Icon from 'components/atoms/icon/Icon'
import Label from 'components/atoms/input/input-label/InputLabel'
import RadioButton from 'components/atoms/radio-button/RadioButton'
import TextArea from 'components/atoms/text-area/TextArea'
import PrimaryDatePicker from 'components/molecules/datepicker/PrimaryDatePicker'
import FileDropZone from 'components/molecules/file-dropzone/FileDropZone'
import { customStyles } from 'lib/helpers/configs/customStyles'
import { IconType } from 'lib/helpers/constants/iconTypes'
import withModal from 'lib/helpers/hoc/withModal'
import { useCreateLeaveRequest } from 'lib/hooks/mutations/Employee/useCreateLeaveRequest'
import { useGenerateTimeOptions } from 'lib/hooks/shared/useGenerateTimeOptions'
import React, { FC, useEffect } from 'react'
import { Control, Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'


interface IProps {
    onConfirm: () => void
}

const CreateLeaveRequest: FC<IProps> = ({ onConfirm }) => {

    const { t } = useTranslation()

    const { timeOptions } = useGenerateTimeOptions();

    const {
        control,
        watch,
        handleSubmit,
        reset,
        register,
        setValue,
        formState: { errors }

    } = useForm({
        defaultValues: {
            absence_type: "",
            one_day_leave: 1,
            has_time: 0,
            start_date: "",
            end_date: "",
            start_time: "",
            end_time: "",
            comment: "",
            file: null,
        }
    })

    const absenceType = watch("absence_type");
    const one_day_leave = watch("one_day_leave");
    const has_time = watch("has_time");

    useEffect(() => {
        if (absenceType !== "Illness") {
            setValue("file", null);
        }

    }, [absenceType, setValue]);

    useEffect(() => {

        if (one_day_leave === 0) {
            setValue("end_date", "");
        }

    }, [one_day_leave, setValue]);

    useEffect(() => {

        if (has_time === 0) {
            setValue("start_time", "");
            setValue("end_time", "");
        }

    }, [has_time, setValue]);

    const { mutateAsync, isLoading } = useCreateLeaveRequest()

    const { id } = useParams()


    const SubmitLeaveRequest = (data: any) => {
        const formData = new FormData();

        for (const key in data) {

            if (key === "file" && data.file !== null) {
                formData.append("file", data.file[0] as FileList[0]);
                continue
            }

            formData.append(key, data[key]);
        }

        formData.append("id", id as string);

        mutateAsync(formData).then(() => {
            onConfirm()
        })
    }

    return (
        <Form className='grid gap-5 grid-cols-1' onSubmit={handleSubmit(SubmitLeaveRequest)}>
            <div className="relative">
                <Label text={t("Type")} />
                <Controller
                    name="absence_type"
                    rules={{ required: "This field is required" }}
                    control={control}
                    render={({ field }) => (
                        <CreatableSelect<any, false>
                            {...field}
                            placeholder={t("Choose type")}
                            options={[
                                { label: t("Vacation"), value: "Vacation" },
                                { label: t("Illness"), value: "Illness" },
                                { label: t("Accident"), value: "Accident" },
                                { label: t("Maternity / Paternity Leave"), value: "Maternity / Paternity Leave" },
                                { label: t("Military / Civil Service"), value: "Military / Civil Service" },
                                { label: t("Medical or Dental Appointment"), value: "Medical or Dental Appointment" },
                                { label: t("Bereavement (Family Death)"), value: "Bereavement (Family Death)" },
                                { label: t("Unpaid Leave"), value: "Unpaid Leave" },
                                { label: t("Overtime Compensation (Time Off)"), value: "Overtime Compensation (Time Off)" },
                                { label: t("Training / Education Leave"), value: "Training / Education Leave" }
                            ]
                            }
                            value={
                                field?.value
                                    ? {
                                        value: field?.value,
                                        label: t(field?.value),
                                    }
                                    : null
                            }
                            className="cursor-pointer font-inter-regular react-select-container"
                            classNamePrefix="react-select"
                            onChange={(newValue: any, actionMeta: any) => {
                                if (
                                    actionMeta.action === "select-option" &&
                                    newValue?.value
                                ) {
                                    field.onChange(newValue.value);
                                }
                            }}
                            styles={customStyles}
                            isValidNewOption={() => false}
                            menuPosition={"fixed"}
                            menuPortalTarget={document.body}
                        />
                    )}
                />
                {errors.absence_type?.message && (
                    <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                        {errors.absence_type?.message as string}
                    </div>
                )}
            </div>
            <div>
                <Label text={t("Leave Request for One day?")} />
                <div className='grid grid-cols-2 gap-4'>
                    <Controller
                        name='one_day_leave'
                        control={control}
                        render={({ field }) => (
                            <RadioButton
                                label={t('Yes')}
                                isChecked={field.value === 1}
                                onChange={() => field.onChange(1)}
                                labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                iconWrapperClassName="!top-[20px] sm:!top-[19px] 2xl:!top-[22px] !right-[6px]"
                                className="right-[8px]"
                            />
                        )}
                    />
                    <Controller
                        name='one_day_leave'
                        control={control}
                        render={({ field }) => (
                            <RadioButton
                                label={t('No')}
                                isChecked={field.value === 0}
                                onChange={() => field.onChange(0)}
                                labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                iconWrapperClassName="!top-[20px] sm:!top-[19px] 2xl:!top-[22px] !right-[6px]"
                                className="right-[8px]"
                            />
                        )}
                    />
                </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
                <div className={`${watch("one_day_leave") === 1 ? "col-span-2" : ""}`}>
                    <Controller
                        control={control}
                        name="start_date"
                        rules={{ required: `${t("This field is required")}` }}
                        render={({ field: { onChange, value } }) => (
                            <PrimaryDatePicker
                                label={watch("one_day_leave") === 1 ? t("Date") : t("Date From")}
                                startDate={value}
                                {...register("start_date")}
                                setStartDate={(date: string) => onChange(date)}
                                error={errors.start_date}
                            />
                        )}
                    />
                </div>
                {watch("one_day_leave") === 0 && (
                    <Controller
                        control={control}
                        name="end_date"
                        rules={{ required: `${t("This field is required")}` }}
                        render={({ field: { onChange, value } }) => (
                            <PrimaryDatePicker
                                label={t("Date To")}
                                startDate={value}
                                {...register("end_date")}
                                setStartDate={(date: string) => onChange(date)}
                                error={errors.end_date}
                            />
                        )}
                    />
                )}
            </div>
            <div>
                <Label text={t("You Want To Add Time ?")} />
                <div className='grid grid-cols-2 gap-4'>
                    <Controller
                        name='has_time'
                        control={control}
                        render={({ field }) => (
                            <RadioButton
                                label={t('Yes')}
                                isChecked={field.value === 1}
                                name='has_time'
                                onChange={() => field.onChange(1)}
                                labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                iconWrapperClassName="!top-[19px] sm:!top-[20px] 2xl:!top-[22px] !right-[6px]"
                                className="right-[8px]"
                            />
                        )}
                    />
                    <Controller
                        name='has_time'
                        control={control}
                        render={({ field }) => (
                            <RadioButton
                                label={t('No')}
                                isChecked={field.value === 0}
                                name='has_time'
                                onChange={() => field.onChange(0)}
                                labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                iconWrapperClassName="!top-[19px] sm:!top-[20px] 2xl:!top-[22px] !right-[6px]"
                                className="right-[8px]"
                            />
                        )}
                    />
                </div>
            </div>

            {watch("has_time") === 1 && (
                <div className='grid grid-cols-2 gap-4'>
                    <div className="relative">
                        <Label text={t("Time from")} />
                        <Controller
                            name="start_time"
                            control={control}
                            rules={{ required: "This field is required" }}
                            render={({ field }) => (
                                <CreatableSelect<any, false>
                                    {...field}
                                    placeholder={"00:00"}
                                    options={timeOptions.map((language) => ({
                                        value: language.value,
                                        label: language.label,
                                    }))}
                                    value={
                                        field.value
                                            ? { value: field.value, label: field.value }
                                            : null
                                    }
                                    className="cursor-pointer font-inter-regular react-select-container"
                                    classNamePrefix="react-select"
                                    onChange={(newValue: any, actionMeta: any) => {
                                        if (
                                            actionMeta.action === "select-option" &&
                                            newValue?.value
                                        ) {
                                            field.onChange(newValue.value);
                                        }
                                    }}
                                    styles={customStyles}
                                    isValidNewOption={() => false}
                                    menuPosition={"fixed"}
                                    menuPortalTarget={document.body}

                                />
                            )}
                        />
                        {errors.start_time?.message && (
                            <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-16px]  left-[2px]">
                                <>{errors.start_time?.message}</>
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        <Label text={t("Time to")} />
                        <Controller
                            name="end_time"
                            control={control}
                            rules={{ required: "This field is required" }}
                            render={({ field }) => (
                                <CreatableSelect<any, false>
                                    {...field}
                                    placeholder={"00:00"}
                                    options={timeOptions.map((language) => ({
                                        value: language.value,
                                        label: language.label,
                                    }))}
                                    value={
                                        field.value
                                            ? { value: field.value, label: field.value }
                                            : null
                                    }
                                    className="cursor-pointer font-inter-regular react-select-container"
                                    classNamePrefix="react-select"
                                    onChange={(newValue: any, actionMeta: any) => {
                                        if (
                                            actionMeta.action === "select-option" &&
                                            newValue?.value
                                        ) {
                                            field.onChange(newValue.value);
                                        }
                                    }}
                                    styles={customStyles}
                                    isValidNewOption={() => false}
                                    menuPosition={"fixed"}
                                    menuPortalTarget={document.body}

                                />
                            )}
                        />
                        {errors.end_time?.message && (
                            <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-16px]  left-[2px]">
                                <>{errors.end_time?.message}</>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <TextArea
                label={t("Comment")}
                placeholder={t("Write a comment")}
                {...register("comment")}
                error={errors.comment}
            />
            {watch("absence_type") === "Illness" && (
                <div>              
                    <Controller
                        control={control}
                        name="file"
                        rules={{ required:false }}
                        render={({ field: { onChange, value } }) => (
                            <FileDropZone
                                file={value}
                                setFile={onChange}
                                label=""
                                formats='.jpg, .png, .pdf, .jpeg'
                                classes={{ label: "bg-white" }}
                                name="file"
                                error={errors.file}
                                control={control as Control<any>}
                            />
                        )}
                    />
                    <div className="flex items-center gap-2 mt-4">
                        <Icon iconType={IconType.ALERT_ICON} color={'var(--theme)'} />
                        <span className="font-inter-medium text-[13px] text-primaryTextColor">
                        {t('If there is no file uploaded, a new HR pending will be created')}
                        </span>
                    </div>
                </div>
                
            )}
            <div className="pt-5 flex justify-end gap-4">
                <IconButton type='button' secondary onClick={() => { reset() }} className='min-w-[120px]' disabled={isLoading}>
                   {t('Reset')}
                </IconButton>
                <IconButton className='min-w-[120px]' disabled={isLoading}>
                    {t('Submit')}
                </IconButton>
            </div>
        </Form>
    )
}

export default withModal(CreateLeaveRequest)